import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { isEmpty } from 'lodash'
import { IconPlaylistAdd, IconPlus } from '@tabler/icons-react'

import { SearchableDropdown } from '@/components/dropdowns/SearchableDropdown'
import {
  fetchPlaylists,
  addCallsToPlaylist,
  createPlaylist,
  fetchPlaylistsByCallId,
  fetchPlaylistsByMessageId,
} from '@/reducers/playlists/playlists.actions'
import { BasicModal } from '@/components/layout/modals/BasicModal'
import { AbstractForm } from '@/components/forms/formik/AbstractForm'

export const AddToPlaylist = ({
  callIds = [],
  handleDeselectAll,
  organizationId,
  shouldFetch = true,
  callType = 'call',
  ...props
}) => {
  const [newPlaylistModalOpen, setNewPlaylistModalOpen] = useState(false)
  const { playlists, loading } = useSelector((state) => state.playlists)
  const dispatch = useDispatch()
  const { id: callId } = useParams()

  const playlistOptions = playlists
    .filter((playlist) => playlist.type !== 'coaching')
    .map((playlist) => ({
      key: playlist.uuid,
      label: playlist.name,
      value: playlist.uuid,
    }))

  const handleOpenNewPlaylistModal = async () => {
    setNewPlaylistModalOpen(true)
  }

  const handleCloseNewPlaylistModal = async () => {
    setNewPlaylistModalOpen(false)
  }

  const handleAddToPlaylist = async (playlistUuid) => {
    if (playlistUuid && !isEmpty(callIds)) {
      await dispatch(addCallsToPlaylist(playlistUuid, organizationId, callIds, false))

      if (handleDeselectAll) {
        handleDeselectAll()
      }

      if (callId) {
        if (callType !== 'call') {
          await dispatch(fetchPlaylistsByMessageId(callId))
        } else {
          await dispatch(fetchPlaylistsByCallId(callId))
        }
      }
    }
  }

  const handleCreatePlaylist = async (values) => {
    if (!isEmpty(values.name) && !isEmpty(callIds)) {
      await dispatch(createPlaylist({ name: values.name, organizationId, callIds }))

      if (handleDeselectAll) {
        handleDeselectAll()
      }

      if (newPlaylistModalOpen) {
        handleCloseNewPlaylistModal()
      }
    }
  }

  const actions = [
    {
      key: 'new-playlist',
      fn: handleOpenNewPlaylistModal,
      label: (
        <div className="flex-align-center small-gap">
          <IconPlaylistAdd className="icon-svg status-brand" />
          Add to New Playlist
        </div>
      ),
    },
  ]

  useEffect(() => {
    if (shouldFetch && organizationId) {
      dispatch(fetchPlaylists(organizationId))
    }
  }, [shouldFetch, organizationId])

  return (
    <>
      <div className="flex-align-center medium-gap">
        <SearchableDropdown
          buttonLabel="Playlist"
          buttonProps={{ small: true }}
          additionalIcon={<IconPlus />}
          inputProps={{ placeholder: 'Search...' }}
          options={playlistOptions}
          loading={loading.playlists}
          handleSelect={handleAddToPlaylist}
          actions={actions}
          dataTestId="playlist"
          {...props}
        />
      </div>

      {newPlaylistModalOpen && (
        <BasicModal
          data-testid="add-to-new-playlist"
          title="New Playlist"
          onClose={handleCloseNewPlaylistModal}
          show={newPlaylistModalOpen}
          size="tiny"
        >
          <AbstractForm
            schema={[
              {
                name: 'name',
                type: 'text',
                label: 'Playlist Name',
                required: true,
                autoFocus: true,
              },
            ]}
            onSubmit={handleCreatePlaylist}
            buttonLabel="Create Playlist"
            onClose={handleCloseNewPlaylistModal}
            isModal
          />
        </BasicModal>
      )}
    </>
  )
}
